import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Payouts = ({ darkMode }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const bg = darkMode ? '#111827' : '#f9fafb';
  const card = darkMode ? '#1f2937' : '#fff';
  const text = darkMode ? '#f9fafb' : '#111827';
  const subText = darkMode ? '#9ca3af' : '#6b7280';
  const border = darkMode ? '#374151' : '#d1d5db';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchPayouts();
  }, [user]);

  const fetchPayouts = async () => {
    try {
      const { data } = await API.get('/payouts/my-payouts');
      setPayouts(data.payouts);
      setStats(data.stats);
    } catch (err) {
      toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  const requestPayout = async (payoutId) => {
    try {
      await API.post('/payouts/request', { payoutId });
      toast.success('Payout requested! Admin will process within 2-3 business days.');
      fetchPayouts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to request payout');
    }
  };

  const statusColor = (status) => {
    if (status === 'paid') return { bg: '#d1fae5', color: '#065f46' };
    if (status === 'requested') return { bg: '#dbeafe', color: '#1e40af' };
    if (status === 'processing') return { bg: '#fef3c7', color: '#92400e' };
    return { bg: darkMode ? '#374151' : '#f3f4f6', color: subText };
  };

  const filteredPayouts = activeTab === 'all' ? payouts :
    activeTab === 'pending' ? payouts.filter(p => p.status === 'pending') :
    activeTab === 'requested' ? payouts.filter(p => p.status === 'requested') :
    payouts.filter(p => p.status === 'paid');

  if (loading) return <p style={{ textAlign: 'center', padding: '3rem', color: subText }}>Loading earnings...</p>;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem', background: bg, minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: '0 0 0.25rem', color: text }}>My Earnings</h1>
        <p style={{ color: subText, margin: 0 }}>Track your rental income and request payouts</p>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }} className="stats-grid">
        <div style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)', padding: '1.5rem', borderRadius: '16px', color: '#fff' }}>
          <p style={{ fontSize: '0.85rem', margin: '0 0 0.5rem', opacity: 0.85 }}>Total Earnings</p>
          <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '0 0 0.25rem' }}>Rs.{stats.totalEarnings || 0}</p>
          <p style={{ fontSize: '0.75rem', margin: 0, opacity: 0.75 }}>After 10% platform fee</p>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #f59e0b, #ef4444)', padding: '1.5rem', borderRadius: '16px', color: '#fff' }}>
          <p style={{ fontSize: '0.85rem', margin: '0 0 0.5rem', opacity: 0.85 }}>Pending Payout</p>
          <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '0 0 0.25rem' }}>Rs.{stats.pendingEarnings || 0}</p>
          <p style={{ fontSize: '0.75rem', margin: 0, opacity: 0.75 }}>Request to withdraw</p>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #059669, #0891b2)', padding: '1.5rem', borderRadius: '16px', color: '#fff' }}>
          <p style={{ fontSize: '0.85rem', margin: '0 0 0.5rem', opacity: 0.85 }}>Total Paid Out</p>
          <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '0 0 0.25rem' }}>Rs.{stats.paidEarnings || 0}</p>
          <p style={{ fontSize: '0.75rem', margin: 0, opacity: 0.75 }}>Successfully withdrawn</p>
        </div>
      </div>

      {/* Commission Info */}
      <div style={{ background: darkMode ? '#1e3a5f' : '#eff6ff', border: `1px solid ${darkMode ? '#1e40af' : '#bfdbfe'}`, padding: '1rem 1.25rem', borderRadius: '12px', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <p style={{ color: '#1e40af', margin: '0 0 0.2rem', fontWeight: '600' }}>Platform Commission: 10%</p>
          <p style={{ color: darkMode ? '#93c5fd' : '#3b82f6', margin: 0, fontSize: '0.85rem' }}>You keep 90% of every rental payment. Request payout anytime!</p>
        </div>
        <div style={{ background: '#2563eb', color: '#fff', padding: '0.4rem 0.8rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '500' }}>
          {payouts.filter(p => p.status === 'pending').length} pending
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {['all', 'pending', 'requested', 'paid'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: `1px solid ${activeTab === tab ? '#2563eb' : border}`, background: activeTab === tab ? '#2563eb' : card, color: activeTab === tab ? '#fff' : text, cursor: 'pointer', fontWeight: '500', textTransform: 'capitalize', fontSize: '0.85rem' }}>
            {tab} ({tab === 'all' ? payouts.length : payouts.filter(p => p.status === tab).length})
          </button>
        ))}
      </div>

      {/* Payouts List */}
      {filteredPayouts.length === 0 ? (
        <div style={{ background: card, padding: '3rem', borderRadius: '16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <p style={{ fontSize: '1.1rem', color: subText, margin: '0 0 0.5rem' }}>No earnings in this category</p>
          <p style={{ color: subText, fontSize: '0.9rem', margin: 0 }}>Start listing items and confirming bookings to earn!</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filteredPayouts.map(p => (
            <div key={p._id} style={{ background: card, padding: '1.25rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: p.status === 'requested' ? '1px solid #93c5fd' : `1px solid ${border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                    <p style={{ fontWeight: '700', margin: 0, color: text, fontSize: '1.1rem' }}>Rs.{p.payoutAmount}</p>
                    <span style={{ background: statusColor(p.status).bg, color: statusColor(p.status).color, padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '500', textTransform: 'capitalize' }}>{p.status}</span>
                  </div>
                  <p style={{ color: subText, margin: '0 0 0.25rem', fontSize: '0.85rem' }}>
                    Rental total: Rs.{p.totalAmount} � Commission: Rs.{p.commissionAmount} (10%)
                  </p>
                  <p style={{ color: subText, margin: 0, fontSize: '0.8rem' }}>
                    Earned on {new Date(p.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {p.paidAt && ` � Paid on ${new Date(p.paidAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                  </p>
                </div>
                {p.status === 'pending' && (
                  <button onClick={() => requestPayout(p._id)}
                    style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    Request Payout
                  </button>
                )}
                {p.status === 'requested' && (
                  <div style={{ background: '#dbeafe', color: '#1e40af', padding: '0.4rem 0.8rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '500' }}>
                    Processing...
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* How it works */}
      <div style={{ background: card, padding: '1.5rem', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginTop: '2rem' }}>
        <h3 style={{ margin: '0 0 1rem', color: text, fontSize: '1rem' }}>How Payouts Work</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {[
            ['1', 'Renter pays for booking', '#2563eb'],
            ['2', 'Payout record created automatically (90% of rental)', '#7c3aed'],
            ['3', 'Click "Request Payout" to withdraw', '#059669'],
            ['4', 'Admin processes within 2-3 business days', '#f59e0b'],
          ].map(([num, label, color]) => (
            <div key={num} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.8rem', flexShrink: 0 }}>{num}</div>
              <p style={{ margin: 0, color: text, fontSize: '0.9rem' }}>{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Payouts;
